import { ABTestEnv } from '@meitu/ab-test-core';
import { handleMergeAbinfo } from '.';
import * as Sentry from '@sentry/react';
import {
  isTeamSubscription,
  isFromCutout,
  subscribeType,
  SubscribeType,
  isFromPcHome,
  urlSubscribeType,
  setSubscribeTypeSubscription,
  resetSubscribeTypeFromUrl,
  closeSDKAbTest,
} from '@/constants';
import { getIncomeSource } from './vip';
import { getUrlParam } from '@/utils';
import { TabType, ProPeriodTabType } from '@/constants/subscription';
import {
  curTabAtom,
  proPeriodTabAtom,
  isFromChannelBusinessAtom,
  userInfoAtom,
  hasPurchasedMeidouAtom,
  vipInfoAtom,
} from '@/store';
import { getDefaultStore } from 'jotai';
import { IdentifyType, ABTest } from '@meitu/ab-test';

const store = getDefaultStore();

let abTest: InstanceType<typeof ABTest> | undefined;

/**
 * 初始化ab实验
 */
export const initAbTest = async () => {
  try {
    const userInfo = store.get(userInfoAtom);

    // 实例化 ABTest SDK
    const abTestRef = new ABTest({
      appKey: isFromCutout ? '11CB34B59399E101' : 'C5F34252F04B6D50', // 抠图Meepo实验11CB34B59399E101，美图设计室Meepo实验C5F34252F04B6D50
      env: ABTestEnv.Release, // release|beta, beta就是测试环境
      custom: [
        {
          type: IdentifyType.Uid,
          value: userInfo?.id || '',
        },
      ],
    });

    await abTestRef.init(); // 初始化 ABTest SDK

    abTest = abTestRef;

    // 通过 WebSDK 设置 ab_info 的信息
    const localAbInfo = abTest.getABInfo();
    let abInfo = handleMergeAbinfo(localAbInfo);
    window.mtstat?.setABInfo(abInfo);

    // 上报进入实验埋点事件
    // checkIsIn38Meidou();
  } catch (e) {
    console.log('initAbTest error==========', e);
    Sentry.captureException(`ABTest error >>>>>>>: ${e}`);
  }
};

/**
 * 获取合并后的最终abInfo
 */
export const getAbInfo = () => {
  return handleMergeAbinfo(abTest?.getABInfo() || {});
};

/**
 * 高级会员默认美豆第一档还是第二档实验
 */
export const checkIsDefaultSecondTierMeidou = () => {
  // 团队版/PC内嵌，不做实验
  // 高级会员tab下
  const curTab = store.get(curTabAtom);

  if (isTeamSubscription || getIncomeSource() !== 'MTXX-WEB' || curTab !== TabType.PRO_SUB) {
    return false;
  }

  // 实验组
  if (abTest?.isInABTesting(30821) || abTest?.isInABTesting(30826)) {
    return true;
  }

  // 抠图应用和设计室应用
  // 对照组
  if (abTest?.isInABTesting(30820) || abTest?.isInABTesting(30825)) {
    return false;
  }

  return false;
};

/**
 * 新版订阅实验。
 * `isFromChannelBusinessAtom` 须在 `loadSubscriptionEntranceEntries` → `fetchSubscriptionEntranceEntriesSafe`
 * 成功返回后才有意义；初始化链路见 `common.ts` 中 initFn 顺序。
 */
export const checkIsNewUI = () => {
  // 团队版/PC内嵌/抠图张数场景/仅高级会员/渠道策略用户/PC首页，不做实验
  const isFromChannelBusiness = store.get(isFromChannelBusinessAtom);
  if (
    isTeamSubscription ||
    getIncomeSource() !== 'MTXX-WEB' ||
    subscribeType === SubscribeType.CONSUMABLE ||
    subscribeType === SubscribeType.PRO_ONLY ||
    isFromChannelBusiness ||
    isFromPcHome
  ) {
    return false;
  }

  // 实验组
  if (abTest?.isInABTesting(31227) || abTest?.isInABTesting(31275)) {
    return true;
  }

  // 对照组
  if (abTest?.isInABTesting(31226) || abTest?.isInABTesting(31274)) {
    return false;
  }

  return false;
};

/**
 * 38元美豆实验
 */
export const checkIsIn38Meidou = () => {
  // 团队版/PC内嵌/抠图项目/PC首页，不做实验
  if (
    isTeamSubscription ||
    getIncomeSource() !== 'MTXX-WEB' ||
    isFromCutout ||
    isFromPcHome ||
    subscribeType !== SubscribeType.MEIDOU
  ) {
    return false;
  }

  // 实验组
  if (abTest?.isInABTesting(31626)) {
    return true;
  }

  // 对照组
  if (abTest?.isInABTesting(31625)) {
    return false;
  }

  return false;
};

const MEIDOU_REQUIRE_VIP_EXP = 33622;
const MEIDOU_REQUIRE_VIP_CTRL = 33621;

/**
 * 是否属于「未买过美豆」实验人群且入口为美豆场景（用 url 原始类型）
 */
const isMeidouRequireVipAudience = (): boolean => {
  // 团队版/PC内嵌/PC首页/非美豆入口，不做实验
  if (
    isTeamSubscription ||
    getIncomeSource() !== 'MTXX-WEB' ||
    isFromPcHome ||
    urlSubscribeType !== SubscribeType.MEIDOU
  ) {
    return false;
  }

  const purchased = store.get(hasPurchasedMeidouAtom);
  return purchased === false;
};

/**
 * 会员购美豆实验。
 * 副作用：人群内调用 isInABTesting，触发 33622/33621 进入实验埋点事件。
 */
export const checkIsMeidouRequireVip = (): boolean => {
  if (!isMeidouRequireVipAudience()) {
    return false;
  }

  // 实验组
  if (abTest?.isInABTesting(MEIDOU_REQUIRE_VIP_EXP)) {
    return true;
  }

  // 对照组
  if (abTest?.isInABTesting(MEIDOU_REQUIRE_VIP_CTRL)) {
    return false;
  }

  return false;
};

/**
 * 是否应将美豆场景覆盖为会员场景。
 * - 团队版：非会员一律强制（仅限会员可购美豆）
 * - 个人版：实验组且非会员
 */
export const shouldForceSubscriptionScene = (): boolean => {
  if (urlSubscribeType !== SubscribeType.MEIDOU) {
    return false;
  }

  const vipInfo = store.get(vipInfoAtom);
  if (vipInfo?.is_vip) {
    return false;
  }

  // 团队版不走 AB，非会员直接强制购买会员场景
  if (isTeamSubscription) {
    return true;
  }

  return checkIsMeidouRequireVip();
};

/**
 * 根据会员态（及个人版实验）覆盖 / 恢复运行时 subscribeType。
 * 须在 loadSubscriptionEntranceEntries、vip 信息、initMtstat 就绪后、initCurTabValue 之前调用
 *（个人版 isInABTesting 会触发进入实验埋点，依赖埋点 SDK）。
 */
export const applyMeidouRequireVipSceneOverride = (): void => {
  if (urlSubscribeType !== SubscribeType.MEIDOU) {
    return;
  }

  if (shouldForceSubscriptionScene()) {
    setSubscribeTypeSubscription();
  } else {
    resetSubscribeTypeFromUrl();
  }
};

/**
 * 上报进入实验埋点事件
 * 用于服务端实验，接口返回当前命中code，然后由前端来上报进入实验埋点事件
 */
export const reportABCodeEnterTest = (code: number) => {
  if (!code) return;

  abTest?.isInABTesting(code);
};

const PRO_PERIOD_CTRL = 34370;
const PRO_PERIOD_MONTHLY = 34371;
const PRO_PERIOD_ANNUAL = 34372;

/** 高级会员周期实验仅在这些 module_id 下生效 */
const PRO_PERIOD_MODULE_IDS = ['roboneo_web', 'product_kit_web', 'viral_video_web'];

export type ProPeriodExperiment = 'control' | 'monthly' | 'annual' | null;

/** 仅在高级 Tab 门禁通过并完成 isInABTesting 后写入；离开高级不清除 */
let proPeriodExperimentCache: ProPeriodExperiment | undefined;
/** 是否已写入过实验默认周期（会话内只写一次） */
let proPeriodDefaultApplied = false;

const isProPeriodAudienceGate = (): boolean => {
  const curTab = store.get(curTabAtom);
  const moduleId = decodeURIComponent(getUrlParam('module_id') || '');
  if (
    isTeamSubscription ||
    isFromPcHome ||
    getIncomeSource() !== 'MTXX-WEB' ||
    curTab !== TabType.PRO_SUB ||
    !PRO_PERIOD_MODULE_IDS.includes(moduleId) ||
    closeSDKAbTest
  ) {
    return false;
  }
  return true;
};

/**
 * 进入高级 Tab 时调用：门禁 + isInABTesting（副作用上报）并缓存。
 * 门禁未过：返回 null 且不缓存（避免非高级时污染）。
 */
export const checkProPeriodTabExperiment = (): ProPeriodExperiment => {
  if (proPeriodExperimentCache !== undefined) {
    return proPeriodExperimentCache;
  }
  if (!isProPeriodAudienceGate()) {
    return null;
  }

  if (abTest?.isInABTesting(PRO_PERIOD_MONTHLY)) {
    proPeriodExperimentCache = 'monthly';
    return proPeriodExperimentCache;
  }
  if (abTest?.isInABTesting(PRO_PERIOD_ANNUAL)) {
    proPeriodExperimentCache = 'annual';
    return proPeriodExperimentCache;
  }
  if (abTest?.isInABTesting(PRO_PERIOD_CTRL)) {
    proPeriodExperimentCache = 'control';
    return proPeriodExperimentCache;
  }

  proPeriodExperimentCache = null;
  return null;
};

/** 只读缓存，无 isInABTesting 副作用 */
export const getProPeriodExperiment = (): ProPeriodExperiment => {
  return proPeriodExperimentCache === undefined ? null : proPeriodExperimentCache;
};

export const isProPeriodTabExperiment = (): boolean => {
  const v = getProPeriodExperiment();
  return v === 'monthly' || v === 'annual';
};

/**
 * 进入高级路径调用：check + 仅首次为实验组时写入 proPeriodTab 默认值。
 */
export const applyProPeriodTabOnEnter = (): void => {
  const result = checkProPeriodTabExperiment();
  if (proPeriodDefaultApplied) return;
  if (result === 'monthly') {
    store.set(proPeriodTabAtom, ProPeriodTabType.MONTHLY);
    proPeriodDefaultApplied = true;
  } else if (result === 'annual') {
    store.set(proPeriodTabAtom, ProPeriodTabType.ANNUAL);
    proPeriodDefaultApplied = true;
  }
};
